import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Film } from "lucide-react";
import { useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";

if (typeof window !== "undefined") {
  try {
    // Use the worker from the installed package via Vite
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString();
  } catch (error) {
    // Fallback to jsdelivr CDN which typically has better CORS support
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.2.133/build/pdf.worker.min.js";
  }
}

export default function CreateLeftPanel({
  slides,
  activeSlide,
  setActiveSlide,
  isDragging,
  isProcessing,
  slidesAnimationData,
  presentationId,
  setSlides,
  setSlidesAnimationData,
  setAnimatedSlides,
  setIsProcessing,
  setIsDragging,
  setDraggedIndex,
  draggedIndex,
  generateRandomId,
}) {
  const handleAddBlankSlide = () => {
    const newIndex = slides.length;
    const newSlide = {
      id: generateRandomId(),
      type: "blank",
      title: "Animated Slide",
      is_active: true,
      presentationId,
    };
    setSlides((prev) => [...prev, newSlide]);
    setSlidesAnimationData((prev) => ({
      ...prev,
      [newIndex]: {
        elements: [],
        interactions: [],
        totalDuration: 3.0,
        canvasBackground: null,
        canvasBackgroundColor: "#1a1a2e",
      },
    }));
    setAnimatedSlides((prev) => ({ ...prev, [newIndex]: true }));
    setActiveSlide(newIndex);
  };
  const handleUploadImage = (e) => {
    const files = Array.from(e.target.files);
    const newSlides = files.map((file) => ({
      id: generateRandomId(),
      is_active: true,
      type: "image",
      title: file.name,
      image_title: file.name,
      image: file,
      presentationId,
    }));

    setSlides((prev) => [...prev, ...newSlides]);
    if (activeSlide === null && newSlides.length > 0) setActiveSlide(0);
  };

  // ✅ Convert PDF pages into image-type slides
  const processPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const slidesFromPDF = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      const fileBlob = new File([blob], `${file.name}_page_${i}.png`, {
        type: "image/png",
      });

      slidesFromPDF.push({
        id: generateRandomId(),
        is_active: true,
        type: "image", // 👈 treat as image type for API
        title: `${file.name} - Page ${i}`,
        image_title: `${file.name}_page_${i}.png`,
        image: fileBlob,
        presentationId,
      });

      page.cleanup();
    }

    return slidesFromPDF;
  };
  // ✅ Handle PDF file upload
  const handleUploadPDF = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      alert("Please upload a valid PDF file.");
      return;
    }
    setIsProcessing(true);
    try {
      const pages = await processPDF(file);
      setSlides((prev) => [...prev, ...pages]);
      if (activeSlide === null && pages.length > 0) setActiveSlide(0);
    } catch (err) {
      console.error("PDF processing error:", err);
      alert("Failed to process PDF. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };
  // ✅ Drag & drop support
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      for (const file of files) {
        if (file.type === "application/pdf") {
          setIsProcessing(true);
          const pages = await processPDF(file);
          setSlides((prev) => [...prev, ...pages]);
          setIsProcessing(false);
        } else if (file.type.startsWith("image/")) {
          const newSlide = {
            id: Date.now() + Math.random(),
            type: "image",
            title: file.name,
            image_title: file.name,
            is_active: true,
            image: file,
          };
          setSlides((prev) => [...prev, newSlide]);
        }
      }
      if (activeSlide === null && slides.length > 0) setActiveSlide(0);
    },
    [activeSlide, slides.length],
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDropSlide = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const updatedSlides = [...slides];
    const [movedSlide] = updatedSlides.splice(draggedIndex, 1);
    updatedSlides.splice(index, 0, movedSlide);
    setSlides(updatedSlides);
    setDraggedIndex(null);
  };

  const handleDeleteSlide = (id) => {
    setSlides((prevSlides) => {
      const updatedSlides = prevSlides.filter((slide) => slide.id !== id);
      const reorderedSlides = updatedSlides.map((slide, index) => ({
        ...slide,
        order: index + 1,
      }));

      return reorderedSlides;
    });

    setActiveSlide((prevActive) => {
      const newSlides = slides.filter((s) => s.id !== id);
      if (newSlides.length === 0) return null; // no slides left
      if (prevActive === null) return null;
      if (prevActive >= newSlides.length) return newSlides.length - 1; // last slide
      return prevActive; // otherwise, keep current index
    });
  };
  return (
    <div
      className="col-span-3 bg-white border-r border-gray-200 p-4 overflow-y-auto rounded-2xl h-[38rem]"
      style={{ scrollbarWidth: "thin" }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Slides</h2>
        <span className="text-gray-500 text-sm">{slides.length} slides</span>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`w-full h-40 mb-4 flex flex-col items-center justify-center border-2 border-dashed rounded-xl transition ${
          isDragging
            ? "border-purple-500 bg-purple-50"
            : "border-lightPurple bg-gray-50"
        }`}
      >
        <p className="text-gray-500 text-sm mb-2">
          Drag & Drop <span className="font-semibold">PDF or Image</span> here
        </p>

        <div className="flex gap-4 flex-wrap justify-center mt-2">
          <label className="flex items-center justify-center px-4 py-2 bg-lightPurple text-white rounded-md text-sm cursor-pointer hover:bg-purpleColor transition">
            <FontAwesomeIcon icon={faPlus} className="text-xs mr-2" />
            Add PDF
            <input
              type="file"
              accept="application/pdf"
              onChange={handleUploadPDF}
              className="hidden"
            />
          </label>

          <label className="flex items-center justify-center px-4 py-2 bg-lightPurple text-white rounded-md text-sm cursor-pointer hover:bg-purpleColor transition">
            <FontAwesomeIcon icon={faPlus} className="text-xs mr-2" />
            Add Image
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleUploadImage}
              className="hidden"
            />
          </label>

          <button
            onClick={handleAddBlankSlide}
            className="flex items-center justify-center px-4 py-2 bg-lightPurple text-white rounded-md text-sm hover:bg-purpleColor transition"
          >
            <Film className="w-3.5 h-3.5 mr-2" />
            Add Blank Slide
          </button>
        </div>
      </div>

      {isProcessing && (
        <p className="text-center text-sm text-gray-500 my-4">
          Processing PDF, please wait...
        </p>
      )}

      {slides?.map((slide, index) => (
        <div
          key={slide.id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={handleDragOver}
          onDrop={() => handleDropSlide(index)}
          onClick={() => setActiveSlide(index)}
          className={`relative mb-3 rounded-lg cursor-grab transition border overflow-hidden ${
            activeSlide === index
              ? "border-2 border-lightPurple shadow-md"
              : "border border-gray-200 hover:border-gray-400"
          } ${!slide.is_active ? "opacity-60 grayscale-[0.5]" : ""}`}
        >
          {!slide.is_active && (
            <div className="absolute top-1 left-1 bg-gray-500 text-white text-[10px] px-1.5 py-0.5 rounded-md font-bold z-10 flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
              HIDDEN
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSlide(slide.id);
            }}
            className="absolute top-1 right-1 bg-slate-200 font-bold text-black rounded-md w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 hover:text-white transition"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="bg-gray-50 w-full h-40 flex items-start justify-start overflow-hidden text-xs text-gray-600 rounded-lg border border-gray-200">
            {slide.type === "image" && (
              <img
                src={
                  slide.image instanceof File
                    ? URL.createObjectURL(slide.image)
                    : slide.image
                }
                alt={slide.image_title}
                className="object-contain w-full h-full"
              />
            )}

            {slide.type === "blank" && (
              <div
                className="w-full h-full flex flex-col items-center justify-center gap-1.5"
                style={{
                  backgroundColor:
                    slidesAnimationData[index]?.canvasBackgroundColor ||
                    "#1a1a2e",
                }}
              >
                <Film className="w-5 h-5 text-white/40" />
                <span className="text-[10px] text-white/40 font-semibold uppercase tracking-wide">
                  Animated
                </span>
              </div>
            )}

            {(slide.question_type === "multiple" ||
              slide.question_type === "poll") && (
              <div className="p-2 w-full">
                <p className="font-bold text-base mb-2 ml-1 text-black">
                  {slide.title}
                </p>

                <h2 className="text-base font-bold mb-2 ml-1">
                  {slide.question}
                </h2>

                <ul>
                  {slide?.choices?.map((choice, i) => (
                    <li
                      key={i}
                      className="p-2 border rounded-md hover:bg-gray-100 cursor-pointer flex items-center space-x-2 my-2"
                    >
                      <input
                        readOnly
                        type={
                          slide.question_type === "poll" ? "radio" : "checkbox"
                        }
                        name={`choice-${slide.id}`}
                        value={choice.choice_text || choice}
                        className={`h-2 w-2 ${
                          slide.question_type === "poll"
                            ? "form-radio"
                            : "form-checkbox"
                        } text-lightPurple`}
                      />

                      <label className="text-gray-700">
                        {choice.choice_text || choice}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {slide.question_type === "text" && (
              <div className="p-2 w-full">
                <p className="font-bold text-base mb-2 ml-1 text-black">
                  {slide.title}
                </p>

                <h2 className="text-base font-bold mb-2 ml-1">
                  {slide.question}
                </h2>

                <textarea
                  readOnly
                  placeholder="Answer..."
                  className="w-full border rounded p-1 text-[11px] resize-none h-10"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between bg-white px-2 py-1 text-[11px] text-gray-600">
            <span className="font-medium">{index + 1}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
